import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, PackageCheck, Plus, Shirt, Trash2, Undo2 } from 'lucide-react';
import { remettreEquipement, rendreEquipement, supprimerEquipement } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { vide } from '../utils/listes';

const STATUTS = [
  { code: 'prete', label: 'Prêté', aide: 'Le club l\'attend en retour.' },
  { code: 'fourni', label: 'Fourni', aide: 'Donné, il ne reviendra pas.' },
  { code: 'vendu', label: 'Vendu', aide: 'Payé par la famille.' },
  { code: 'autre', label: 'Autre', aide: 'Précisez en commentaire.' },
];

const ETATS = [
  { code: 'neuf', label: 'Neuf' },
  { code: 'bon', label: 'Bon état' },
  { code: 'use', label: 'Usé' },
  { code: 'abime', label: 'Abîmé' },
];

const TONS_STATUT: Record<string, string> = {
  fourni: 'bg-blue-100 text-blue-800',
  prete: 'bg-amber-100 text-amber-800',
  vendu: 'bg-green-100 text-green-800',
  autre: 'bg-gray-100 text-gray-600',
};

function euros(centimes: number): string {
  return `${(centimes / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/**
 * Ce que le club a remis à un adhérent, sur sa fiche.
 *
 * On y vient pour deux gestes : donner un maillot en début de saison, et le
 * réclamer à la fin. Le second est le plus souvent oublié — d'où le rappel
 * quand la date est passée, à l'endroit exact où l'on regarde la personne.
 */
export default function EquipementsAdherent({
  adherent,
  configuration,
}: {
  adherent: any;
  configuration: any;
}) {
  const { aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const peutGerer = aLeDroit('gerer_adherents');

  const [ouvert, setOuvert] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [form, setForm] = useState({
    type: '',
    taille: '',
    numero: '',
    quantite: '1',
    statut: 'prete',
    montant: '',
    etat: 'bon',
    commentaire: '',
  });

  const catalogue: any[] = configuration?.equipements ?? vide<any>();
  const modele = useMemo(() => catalogue.find((c) => c.code === form.type) ?? null, [catalogue, form.type]);
  const equipements: any[] = adherent?.equipements ?? [];

  const annoncer = (type: 'success' | 'error', message: string) => {
    setNotice({ type, message });
    if (type === 'success') setTimeout(() => setNotice(null), 3000);
  };

  const rafraichir = () => {
    queryClient.invalidateQueries({ queryKey: ['adherent', adherent?.id] });
    queryClient.invalidateQueries({ queryKey: ['equipements'] });
  };

  const remettre = useMutation({
    mutationFn: () =>
      remettreEquipement(adherent.id, {
        ...form,
        quantite: Number(form.quantite) || 1,
      }),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      setOuvert(false);
      setForm({ ...form, type: '', taille: '', numero: '', quantite: '1', montant: '', commentaire: '' });
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  const rendre = useMutation({
    mutationFn: ({ id, etatRetour }: { id: number; etatRetour: string }) => rendreEquipement(id, { etatRetour }),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  const retirer = useMutation({
    mutationFn: (id: number) => supprimerEquipement(id),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Shirt className="w-4 h-4 text-primary-600" />
          Équipements
        </h3>
        {peutGerer && catalogue.length > 0 && !ouvert && (
          <button
            onClick={() => setOuvert(true)}
            className="shrink-0 inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800"
          >
            <Plus className="w-4 h-4" />
            Remettre
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Ce que le club lui a fourni, prêté ou vendu — et ce qu'il attend en retour.
      </p>

      {notice && (
        <div
          className={`mb-4 rounded-lg px-3 py-2 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {notice.message}
        </div>
      )}

      {catalogue.length === 0 && (
        <p className="text-sm text-gray-500">
          Aucun équipement au catalogue : déclarez-les dans Administration → Adhérents.
        </p>
      )}

      {equipements.length === 0 ? (
        catalogue.length > 0 && <p className="text-sm text-gray-500">Rien ne lui a été remis.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {equipements.map((e: any) => (
            <li key={e.id} className="flex flex-wrap items-center gap-3 py-2.5 text-sm">
              <span className="flex-1 min-w-0">
                <span className="block text-gray-900">
                  {e.libelle}
                  {e.taille && <span className="text-gray-500"> · {e.taille}</span>}
                  {e.numero && <span className="text-gray-500"> · n° {e.numero}</span>}
                  {e.quantite > 1 && <span className="text-gray-400"> ×{e.quantite}</span>}
                </span>
                <span className="block text-xs text-gray-500">
                  {[
                    `remis le ${e.remisLe}`,
                    e.saison,
                    e.montant > 0 ? euros(e.montant) : null,
                    e.renduLe ? `rendu le ${e.renduLe}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>

              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${
                  TONS_STATUT[e.statut] ?? 'bg-gray-100 text-gray-600'
                }`}
              >
                {e.statutLibelle}
              </span>

              {e.renduLe && <PackageCheck className="w-4 h-4 text-green-600 shrink-0" />}

              {e.enRetard && (
                <span className="shrink-0 inline-flex items-center gap-1 text-xs text-red-700">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  à rendre depuis le {e.rendreAvant}
                </span>
              )}

              {peutGerer && e.attendu && (
                <RendreBouton onRendre={(etat) => rendre.mutate({ id: e.id, etatRetour: etat })} />
              )}

              {peutGerer && (
                <button
                  onClick={() => {
                    if (window.confirm(`Retirer « ${e.libelle} » de l'inventaire ?`)) retirer.mutate(e.id);
                  }}
                  title="Retirer de l'inventaire"
                  className="shrink-0 p-1.5 rounded text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {ouvert && (
        <div className="mt-4 rounded-lg border border-gray-200 p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value, taille: '' })}
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
            >
              <option value="">Quel équipement ?</option>
              {catalogue.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.libelle}
                </option>
              ))}
            </select>

            <select
              value={form.taille}
              onChange={(e) => setForm({ ...form, taille: e.target.value })}
              disabled={modele === null}
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm disabled:bg-gray-100"
            >
              <option value="">Taille…</option>
              {(modele?.tailles ?? []).map((t: string) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>

            <input
              value={form.numero}
              onChange={(e) => setForm({ ...form, numero: e.target.value })}
              placeholder="Numéro floqué"
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
            />

            <input
              type="number"
              min={1}
              value={form.quantite}
              onChange={(e) => setForm({ ...form, quantite: e.target.value })}
              placeholder="Quantité"
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
            />

            <select
              value={form.statut}
              onChange={(e) => setForm({ ...form, statut: e.target.value })}
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
            >
              {STATUTS.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.label}
                </option>
              ))}
            </select>

            <select
              value={form.etat}
              onChange={(e) => setForm({ ...form, etat: e.target.value })}
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
            >
              {ETATS.map((e) => (
                <option key={e.code} value={e.code}>
                  Remis {e.label.toLowerCase()}
                </option>
              ))}
            </select>

            {form.statut === 'vendu' && (
              <input
                value={form.montant}
                onChange={(e) => setForm({ ...form, montant: e.target.value })}
                placeholder="Prix payé (€)"
                inputMode="decimal"
                className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
              />
            )}

            <input
              value={form.commentaire}
              onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
              placeholder="Remarque"
              className="sm:col-span-2 px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
            />
          </div>

          <p className="text-xs text-gray-500">
            {STATUTS.find((s) => s.code === form.statut)?.aide}
            {form.statut === 'prete' && ' La date de retour est celle de la fin de saison.'}
          </p>

          <div className="flex justify-end gap-3">
            <button onClick={() => setOuvert(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">
              Annuler
            </button>
            <button
              onClick={() => remettre.mutate()}
              disabled={remettre.isPending || !form.type}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
            >
              {remettre.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Remettre
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function RendreBouton({ onRendre }: { onRendre: (etat: string) => void }) {
  const [ouvert, setOuvert] = useState(false);

  if (!ouvert) {
    return (
      <button
        onClick={() => setOuvert(true)}
        className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-300 text-xs text-gray-700 hover:bg-gray-50"
      >
        <Undo2 className="w-3.5 h-3.5" />
        Rendu
      </button>
    );
  }

  return (
    <select
      autoFocus
      defaultValue=""
      onChange={(e) => {
        if (e.target.value) {
          onRendre(e.target.value);
          setOuvert(false);
        }
      }}
      onBlur={() => setOuvert(false)}
      className="shrink-0 px-2 py-1 rounded-lg border border-gray-300 text-xs"
    >
      <option value="">Dans quel état ?</option>
      {ETATS.map((e) => (
        <option key={e.code} value={e.code}>
          {e.label}
        </option>
      ))}
    </select>
  );
}
